import { useState, useCallback } from "react";
import { Button, Box, CircularProgress } from "@mui/material";
import { SimpleDialog } from "@powerdiary/components-library/lib";
import StripePaymentForm from "../../../Shared/StripePayment/StripePaymentForm";
import LoadingOverlay from "../../../Shared/LoadingOverlay";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import StripeDetailsView from "../../../Shared/StripePayment/StripeDetailsView";
import { useCustomFormClientCreditCardDetails } from "../../hooks/useCustomFormClientCreditCardDetails";
import { useSaveCreditCard } from "../../hooks/useSaveCreditCard";
import RecaptchaNotice from "../../../Shared/RecaptchaNotice";

export interface SaveCreditCardDialogState {
    isOpen: boolean;
    updateCallback: ((value: string) => void) | null;
}

interface ISaveCreditCardDialogProps {
    onClose: () => void;
    publicId: string;
    practitionerId?: number;
    onCardSaved: (value: string) => void;
    open: boolean;
}

export default function SaveCreditCardDialog({
    practitionerId,
    publicId,
    onClose,
    onCardSaved,
    open,
}: ISaveCreditCardDialogProps) {
    const [editCardMode, setEditCardMode] = useState(false);
    const { isLoading: creditCardInfoLoading, creditCardInfo } = useCustomFormClientCreditCardDetails(
        publicId,
        practitionerId
    );
    const { handleAddCard, isSaving } = useSaveCreditCard({
        publicId,
        practitionerId,
        onCardSaved,
    });
    const stripe = useStripe();
    const elements = useElements();

    const handleEditMode = useCallback(() => {
        setEditCardMode(true);
    }, []);

    const dialogContent = (
        <div data-test-id="stripe_custom_form_field">
            {creditCardInfoLoading ? (
                <LoadingOverlay />
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "8px" }}>
                    {editCardMode || !creditCardInfo?.cardNumber ? (
                        <StripePaymentForm />
                    ) : (
                        <StripeDetailsView creditCardInfo={creditCardInfo} setEditCardMode={handleEditMode} />
                    )}
                    <Button
                        data-test-id="button_add_card"
                        variant="contained"
                        size="medium"
                        onClick={() => {
                            void handleAddCard();
                        }}
                        disabled={isSaving || !stripe || !elements || (!editCardMode && !!creditCardInfo)}
                        startIcon={isSaving ? <CircularProgress size={20} /> : null}
                    >
                        {isSaving ? "Saving" : "Add"}
                    </Button>

                    <RecaptchaNotice />
                </Box>
            )}
        </div>
    );

    return (
        <SimpleDialog
            open={open}
            handleClose={isSaving ? undefined : onClose}
            dialogTitleText="Securely store your card details"
            renderContent={() => dialogContent}
        />
    );
}
